// HTTP request/response contracts for the Worker routes. ARCHITECTURE.md §1.1.
import { z } from 'zod';
import { FactsDigestSchema } from './facts';
import { CoachTextSchema, CoachProvenanceSchema, CoachSlotSchema } from './coach';
import { HandRecordSchema, DecisionRecordSchema, DebriefRecordSchema } from './records';
// POST /api/coach - bucketed facts → cached / freshly-narrated CoachText.
export const CoachRequestSchema = z.object({
    factsDigest: FactsDigestSchema,
    slots: z.array(CoachSlotSchema).optional(),
});
export const CoachResponseSchema = z.object({
    text: CoachTextSchema,
    provenance: CoachProvenanceSchema,
    cached: z.boolean(),
    /** GH5: true when the daily call-cap was already spent, so this response is the deterministic
     *  template fallback served WITHOUT a metered model call (silent, not an error). Absent ⇒ not capped. */
    capped: z.boolean().optional(),
});
// GET /api/coach/usage - the daily narration-budget readout (GH5). `used` counts metered model calls
// today (cache hits are $0 and never counted); `remaining = max(0, cap - used)`.
export const CoachUsageResponseSchema = z.object({
    date: z.string(), // UTC calendar day, YYYY-MM-DD
    used: z.number().int().nonnegative(),
    cap: z.number().int().nonnegative(),
    remaining: z.number().int().nonnegative(),
});
// POST /api/hands - persist a HandRecord.
export const PersistHandRequestSchema = z.object({
    sessionId: z.string(),
    handRecord: HandRecordSchema,
    overallGrade: z.number().optional(),
    debrief: DebriefRecordSchema.optional(),
});
export const PersistHandResponseSchema = z.object({ id: z.string() });
// POST /api/decisions - persist a batch of graded decisions.
export const PersistDecisionsRequestSchema = z.object({
    decisions: z.array(DecisionRecordSchema),
});
export const PersistDecisionsResponseSchema = z.object({ count: z.number().int().nonnegative() });
// GET /api/progress - SQL rollups (ARCHITECTURE.md §9; progress engine).
export const LeakStatementSchema = z.object({
    code: z.string(),
    statement: z.string(),
    leakScore: z.number(),
    count: z.number().int().nonnegative(),
});
export const SkillDimensionAvgSchema = z.object({
    dimension: z.string(),
    avg: z.number(),
    n: z.number().int().nonnegative(),
});
export const ProgressDashboardSchema = z.object({
    totals: z.object({ hands: z.number().int().nonnegative(), decisions: z.number().int().nonnegative() }),
    gradeByStreet: z.record(z.string(), z.number()).optional(),
    gradeByArchetype: z.record(z.string(), z.number()).optional(),
    gradeByStackDepth: z.record(z.string(), z.number()).optional(),
    gradeByPosition: z.record(z.string(), z.number()).optional(),
    gradeByStage: z.record(z.string(), z.number()).optional(), // tournament stage; absent for cash-only
    skillDimensions: z.array(SkillDimensionAvgSchema),
    leaks: z.array(LeakStatementSchema),
    improvements: z.array(z.string()),
    bestSkill: z.string().nullable().optional(),
    biggestLeak: z.string().nullable().optional(),
});
export const ProgressResponseSchema = ProgressDashboardSchema;
// POST /api/import - post-session text → a HandRecord (off-table only; ARCHITECTURE.md §1.1).
export const ImportRequestSchema = z.object({
    text: z.string(),
    format: z.enum(['pokerstars', 'phh', 'auto']).optional(),
});
export const ImportResponseSchema = z.object({ handRecord: HandRecordSchema });
//# sourceMappingURL=api.js.map